import { Component, ElementRef, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonMessages } from '@app/constants/common.messages';
import { TerminologyEntity } from '@app/domain/terminology/model/terminology-entity.enum';
import { Department } from '@app/models/department.model';
import { IState } from '@app/models/state/state.model';
import { CompanyAPIService } from '@app/shared/api/company/company.api.service';
import { Globals } from '@app/shared/globals/globals';
import { debounceTime, map } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

interface PageState extends IState {
  searchRunning: boolean;
  dropdownOpen: boolean;
  disabled: boolean;
  searchIsEmpty: boolean;
}

type PickerValue = (Department | Department[]);

@Component({
  selector: 'app-department-picker',
  templateUrl: './department-picker.component.html',
  styleUrls: ['./department-picker.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DepartmentPickerComponent),
    multi: true,
  }],
})
export class DepartmentPickerComponent implements OnInit, ControlValueAccessor {

  public readonly eTerminologyEntity = TerminologyEntity;
  public readonly eCommonMessages = CommonMessages;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  @Input() searchPlaceholder: string;  
  @Input() customOptions: Department[];
  @Input() customOptionsOnly: boolean;
  @Input() canSelectMultiple: boolean;
  @Input() blacklistedIds: number[];
  @Input() showClearIcon: boolean;
  @Input() loseFocusAfterSelection: boolean;
  @Input() clearSearchAfterSelection: boolean;

  state: PageState;
  searchControl: UntypedFormControl;
  results: Department[];
  _value: Department[];
  searchIsFocused: boolean;

  onChange = (_: any) => {};
  onTouched = () => {};

  get value(): PickerValue {
    return (this.canSelectMultiple ? this._value : this._value[0]);
  }

  set value(v: PickerValue) {
    if (this.state.disabled) { return; }
    this.writeValue(v);
    this.onChange(v);
  }

  constructor(
    public globals: Globals,
    private companyAPIService: CompanyAPIService
  ) {
    this.searchControl = new UntypedFormControl();
    this._value = [];
    this.results = [];
    this.blacklistedIds = [];

    this.searchPlaceholder = CommonMessages.SEARCH_BY_NAME;

    this.customOptions = [];
    this.customOptionsOnly = false;
    this.canSelectMultiple = false;
    this.showClearIcon = true;

    this.state = {
      loading: true,
      error: false,
      errorMessage: '',
      searchRunning: false,
      dropdownOpen: false,
      disabled: false,
      searchIsEmpty: true
    };
    this.searchIsFocused = false;
    this.loseFocusAfterSelection = true;
    this.clearSearchAfterSelection = true;
  }

  ngOnInit(): void {
    this.state.loading = false;
    this.searchControl.valueChanges
      .pipe(
        map(sarg => {
          this.state.searchRunning = true;
          if (sarg) {
            sarg = sarg.trim().toLowerCase();
          }
          return sarg;
        }),
        debounceTime(500)
      )
      .subscribe(sarg => this.trySearch(sarg));
  }

  trySearch(sarg: string): void {
    this.state.searchRunning = true;

    this.state.searchIsEmpty = !(sarg && sarg.length > 0);

    if (this.customOptionsOnly) {
      this.doSearchCustomOnly(this.customOptions, sarg);
      return;
    } else {
      this.doSearchRegular(this.customOptions, sarg);
    }

  }
  
  doSearchCustomOnly(customOptions: Department[], sarg: string): void {
    let results = customOptions;
    if (sarg) {
      results = results.filter(s => s.name.toLowerCase().includes(sarg)); // Only matches to search
    }
    results = results.slice(0, 5); // Only first 5
    const selectedDepartmentIds = this._value.map(v => v.id);
    results = results.filter(r => !selectedDepartmentIds.includes(r.id)); // Not already selected
    results = results.filter(r => !this.blacklistedIds.includes(r.id)); // Not blacklisted

    this.results = results;
    this.state.searchRunning = false;
    this.state.loading = false;
  }

  doSearchRegular(customOptions: Department[], sarg: string): void {
    let matchingCustomResults = [];
    if (sarg) {
      matchingCustomResults = customOptions.filter(s => s.name.toLowerCase().includes(sarg));
    }

    firstValueFrom(
      this.companyAPIService.getDepartments(null, sarg)
    )
      .then(results => {
        results = [...matchingCustomResults, ...results ];
        const selectedDepartmentIds = this._value.map(v => v.id);
        results = results.filter(r => !selectedDepartmentIds.includes(r.id));
        results = results.filter(r => !this.blacklistedIds.includes(r.id));
        this.results = results.slice(0, 5);
      })
      .finally(() => {
        this.state.searchRunning = false;
        this.state.loading = false;
      });
  }

  selectItem(department: Department): void {
    if (this.state.disabled) { return; }

    if (this.canSelectMultiple) {
      this.addItemToMultiplePickerValue(department);
    } else {
      this.value = department;
      this.state.dropdownOpen = false;
    }

    this.blurSearchIfToggled();
    this.clearSearchIfToggled();

    this.onTouched();
    this.searchControl.updateValueAndValidity();
  }

  blurSearchIfToggled(): void {
    if (!this.loseFocusAfterSelection) {
      this.focusSearchInput();
    } else {
      this.blurSearchInput();
    }
  }

  clearSearchIfToggled(): void {
    if (this.clearSearchAfterSelection) {
      this.searchControl.patchValue('');
    }
  }

  addItemToMultiplePickerValue(department: Department): void {
    const selectedIds = this._value.map(r => r.id);
    if (selectedIds.includes(department.id)) { return; }
    this.value = [...this._value, department];
  }

  removeSelectedItem(department: Department, event?: MouseEvent): void {
    if (!this.canSelectMultiple) { return; }
    if (event) {
      event.stopPropagation();
    }
    this.value = this._value.filter(r => r.id !== department.id);
  }

  writeValue(v: PickerValue): void {
    if (this.canSelectMultiple) {
      v = v || [];
      this._value = v as Department[];
    } else {
      if (v) {
        this._value = [v as Department];
      } else {
        this._value = [];
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.state.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  onFocusSearch(): void {
    this.searchIsFocused = true;
    if (this.state.disabled) { return; }

    this.state.dropdownOpen = true;
  }

  onBlurSearch(): void {
    this.searchIsFocused = false;

    setTimeout(() => {
      if (this.searchIsFocused) { return; }
      this.state.dropdownOpen = false;
    }, 200);
  }

  trySelectFirst() {
    if (this.results.length === 0) {
      if (!this.searchControl.value) { return this.searchControl.updateValueAndValidity(); }
      return;
    } // Can't select first if there are zero results

    this.selectItem(this.results[0]);

    if (!this.canSelectMultiple) {
      this.blurSearchInput();
    }
  }

  focusSearchInput(): void {
    if (!this.searchInput) { return; }
    this.searchInput.nativeElement.focus();
    this.state.dropdownOpen = true;
  }

  blurSearchInput(): void {
    if (!this.searchInput) { return; }
    this.searchInput.nativeElement.blur();
    this.state.dropdownOpen = false;
  }

  onKeyupBackspace(): void {
    if (this.searchControl.value) { return; }
    if (!this.canSelectMultiple) { return; }
    if (!this.state.searchIsEmpty) { return; }
    if (this.state.searchRunning) { return; }

    // Get value
    const value = this.value as Department[];

    if (!value) { return; }
    if (value.length === 0) { return; }

    value.pop();

    this.value = value;
    this.searchControl.updateValueAndValidity();
  }

  onKeyupEscape(): void {
    this.blurSearchInput();
  }

  clearInput(): void {
    const customOptionWithNullId = this.customOptions.find(c => c.id === null);

    if (customOptionWithNullId) {
      if (this.canSelectMultiple) {
        this.value = [ customOptionWithNullId ];
      } else {
        this.value = customOptionWithNullId;
      }
    } else {
      if (this.canSelectMultiple) {
        this.value = [];
      } else {
        this.value = null;
      }
    }

    this.searchControl.updateValueAndValidity();
  }
}
